import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  safePolygon,
  shift,
  size,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useId,
  useInteractions,
  useMergeRefs,
} from '@floating-ui/react';
import clsx from 'clsx';
import { memo, useRef, useState, type ReactNode } from 'react';

import type { SideNavLink } from '../types';

import { resolveTopLayerRoot } from '../../../../../utils/resolveTopLayerRoot';
import { useSideNavContext } from '../context';
import { isLinkCurrent } from '../utils';
import { SideNavControl } from './SideNavControl';

interface SideNavFlyoutProps {
  /** The rail control the flyout is anchored to. */
  children: ReactNode;
  /** The parent section's label, shown as the flyout's overline header. */
  label: string;
  links: SideNavLink[];
}

/**
 * Nested links for the rail, where there is no room to expand a section
 * inline. Opens on hover and on keyboard focus, closes on Escape or when the
 * pointer leaves for good, and renders in the top layer so the nav's scroll
 * container never clips it.
 */
export const SideNavFlyout = memo(function SideNavFlyout({
  children,
  label,
  links,
}: SideNavFlyoutProps) {
  const { activeKey, onActivate, regionAttributes } = useSideNavContext();
  const [isOpen, setIsOpen] = useState(false);
  const [portalRoot, setPortalRoot] = useState<HTMLElement | undefined>();
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const headerId = useId();

  const { context, floatingStyles, refs } = useFloating({
    middleware: [
      offset(4),
      flip({ fallbackPlacements: ['right-end'], mainAxis: false }),
      shift({ crossAxis: true, mainAxis: false, padding: 8 }),
      size({
        apply({ availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${Math.max(80, availableHeight)}px`,
          });
        },
        padding: 8,
      }),
    ],
    open: isOpen,
    placement: 'right-start',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    onOpenChange: (nextOpen) => {
      if (nextOpen) {
        setPortalRoot(resolveTopLayerRoot(anchorRef.current));
      }
      setIsOpen(nextOpen);
    },
  });

  const hover = useHover(context, {
    handleClose: safePolygon(),
    move: false,
  });
  const focus = useFocus(context, { visibleOnly: true });
  const dismiss = useDismiss(context);
  const { getFloatingProps, getReferenceProps } = useInteractions([
    hover,
    focus,
    dismiss,
  ]);
  const anchorRefs = useMergeRefs([refs.setReference, anchorRef]);

  return (
    <>
      <div
        className={clsx(
          'eds-side-nav--flyout-anchor',
          isOpen && 'eds-side-nav--flyout-anchor--open',
        )}
        ref={anchorRefs}
        {...getReferenceProps()}
      >
        {children}
      </div>
      {isOpen && (
        <FloatingPortal root={portalRoot}>
          <FloatingFocusManager
            context={context}
            initialFocus={-1}
            modal={false}
            returnFocus={false}
          >
            <div
              aria-labelledby={headerId}
              className="eds-side-nav--flyout"
              ref={refs.setFloating}
              role="group"
              style={floatingStyles}
              {...regionAttributes.flyout}
              {...getFloatingProps()}
            >
              <span className="eds-side-nav--flyout-header" id={headerId}>
                {label}
              </span>
              {links.map((link) => (
                <SideNavControl
                  className="eds-side-nav--flyout-item"
                  isCurrent={isLinkCurrent(link.key, activeKey)}
                  key={link.key}
                  link={link}
                  onActivate={(key) => {
                    setIsOpen(false);
                    onActivate(key);
                  }}
                >
                  <span className="eds-side-nav--flyout-item-label">
                    {link.label}
                  </span>
                </SideNavControl>
              ))}
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
});
